import { math, Vec3 } from 'playcanvas';

import type { WalkAvatarState } from '../avatar/AvatarTypes';
import type { Collision, PushOut } from '../collision';
import type { CameraFrame, Camera, CameraController } from './camera';
import { DEFAULT_CONTROLLER_DAMPING, applyFrameRotation, dampAngles, setBasisOffset, setYawBasis } from './camera-utils';
import { SpawnState } from './spawn-state';
import { findCylinderSpawn } from '../collision/find-spawn';
import { damp } from '../core/math';

const FIXED_DT = 1 / 60;
const MAX_SUBSTEPS = 10;

/** Pre-allocated push-out vector for capsule collision */
const out: PushOut = { x: 0, y: 0, z: 0 };

const v = new Vec3();
const d = new Vec3();

const forward = new Vec3();
const right = new Vec3();
const moveStep = [0, 0, 0];

const offset = new Vec3();
const spawnProbe = new Vec3();
const avatarFoot = new Vec3();
const cameraFocus = new Vec3();
const cameraPosition = new Vec3();
const cameraDirection = new Vec3();
const cameraUpOffset = new Vec3();

/**
 * First-person camera controller with spring-damper suspension over collision terrain.
 *
 * Movement is constrained to the horizontal plane (XZ) relative to the camera yaw.
 * Vertical positioning uses a spring-damper system that hovers the capsule above the
 * collision surface, filtering out terrain noise for smooth camera motion. Capsule
 * collision handles walls and obstacles. When airborne, normal gravity applies.
 */
class WalkController implements CameraController {
    /**
     * Optional collision for capsule collision with sliding
     */
    collision: Collision | null = null;

    /**
     * Field of view in degrees for walk mode.
     */
    fov = 90;

    /**
     * Total capsule height in meters (default: human proportion)
     */
    capsuleHeight = 1.5;

    /**
     * Capsule radius in meters
     */
    capsuleRadius = 0.2;

    /**
     * Camera height from the bottom of the capsule in meters
     */
    eyeHeight = 1.3;

    /**
     * Gravity acceleration in m/s^2
     */
    gravity = 9.8;

    /**
     * Jump velocity in m/s
     */
    jumpSpeed = 4;

    /**
     * 起跳瞬间水平速度上限（m/s）。默认 Infinity 不限制（walk 模式不受影响）。
     * Avatar 模式设为有限值可截顶跑跳的高速度，走跳速度低于上限时不受影响。
     */
    maxJumpHorizontalSpeed = Infinity;

    /**
     * Movement speed in m/s when grounded
     */
    moveGroundSpeed = 7;

    /**
     * Movement speed in m/s when in the air (for air control)
     */
    moveAirSpeed = 1;

    /**
     * Rotation damping factor (0 = no damping, 1 = full damping)
     */
    rotateDamping = DEFAULT_CONTROLLER_DAMPING;

    /**
     * Velocity damping factor when grounded (0 = no damping, 1 = full damping)
     */
    velocityDampingGround = 0.99;

    /**
     * Velocity damping factor when in the air (0 = no damping, 1 = full damping)
     */
    velocityDampingAir = 0.998;

    /**
     * Target clearance from capsule bottom to ground surface in meters.
     * The capsule hovers this far above terrain to avoid bouncing on noisy surfaces.
     */
    hoverHeight = 0.2;

    /**
     * Spring stiffness for ground-following suspension (higher = stiffer tracking).
     */
    springStiffness = 800;

    /**
     * Damping coefficient for ground-following suspension.
     * Critical damping is approximately 2 * sqrt(springStiffness).
     */
    springDamping = 57;

    /**
     * Maximum downward raycast distance to search for ground below the capsule.
     */
    groundProbeRange = 1.0;

    /** 是否启用第三人称 Avatar 相机。 */
    thirdPersonEnabled = false;

    /** 第三人称相机与角色焦点的距离。 */
    thirdPersonDistance = 3.2;

    /** 第三人称相机注视的角色高度。 */
    thirdPersonFocusHeight = 1.05;

    /** 给输入系统使用的移动参考距离，避免第三人称相机距离放大移动。 */
    inputDistance = 1;

    private _position = new Vec3();

    private _prevPosition = new Vec3();

    /**
     * 渲染插值位置：在每帧 update 末尾由 _prevPosition / _position 按 alpha 插值得到，
     * 与相机渲染位置使用同一个流，给 Avatar 模型用，避免"相机平滑/模型阶梯"导致抖动。
     */
    private _interpPosition = new Vec3();

    private _angles = new Vec3();

    private _targetAngles = new Vec3();

    private _distance = 1;

    private _spawn = new SpawnState();

    private _spawnGrounded = false;

    private _velocity = new Vec3();

    private _pendingMove = [0, 0, 0];

    private _accumulator = 0;

    private _grounded = false;

    private _jumping = false;

    private _jumpHeld = false;

    private _moveMagnitude = 0;

    /** 第三人称下角色独立 yaw（度），与相机 yaw 解耦，参考 hunter-world。 */
    private _avatarYaw = 0;

    /** 角色 yaw 平滑旋转的阻尼系数，越高越平滑。 */
    avatarRotationDamping = DEFAULT_CONTROLLER_DAMPING;

    private _jumpStarted = false;

    private _jumpHadInput = false;

    /** 第三人称相机当前距离（平滑后），用于避免命中墙体瞬间的距离突变。 */
    private _thirdPersonCurrentDistance = -1;

    /** 第三人称相机收近时的阻尼（damp() 中：越接近 1 越慢）。约 0.2s 收紧 90%。 */
    thirdPersonRetractDamping = 0.988;

    /** 第三人称相机伸远恢复时的阻尼（比收近慢，避免出墙瞬间猛拉远）。约 0.5s 恢复 90%。 */
    thirdPersonExtendDamping = 0.995;

    private _debugFrame = 0;

    onEnter(camera: Camera): void {
        this.goto(camera);
        if (this.collision) {
            // Spawn is scoped to this walk-mode entry; reset so a stale spawn
            // from a previous entry can't be restored if this entry fails.
            this._spawn.clear();
            // Treat the walk capsule as a tight-superset cylinder for spawn.
            // The carve was produced with a separable XYZ dilation (flat ends,
            // no hemispheres), so cylinder math matches the data exactly.
            // Include `hoverHeight` in the height so the ceiling-clearance
            // check accounts for the placed capsule's full vertical envelope:
            // foot sits at `floor + hoverHeight`, head at `floor + hoverHeight
            // + capsuleHeight`.
            if (findCylinderSpawn(this.collision,
                camera.position.x, camera.position.y, camera.position.z,
                (this.capsuleHeight + this.hoverHeight) * 0.5, this.capsuleRadius, spawnProbe)) {
                // spawnProbe is the floor world position the cylinder rests
                // on. Eye sits hoverHeight + eyeHeight above the floor.
                this._position.set(
                    spawnProbe.x,
                    spawnProbe.y + this.hoverHeight + this.eyeHeight,
                    spawnProbe.z
                );
                this._grounded = true;
                this._velocity.y = 0;
                this._storeSpawn();
            }

            this._prevPosition.copy(this._position);
            this._interpPosition.copy(this._position);
        }
    }

    update(deltaTime: number, inputFrame: CameraFrame, camera: Camera) {
        const { move, rotate } = inputFrame.read();

        // apply rotation at display rate for responsive mouse look
        applyFrameRotation(this._targetAngles, rotate, this.thirdPersonEnabled ? -35 : -90, this.thirdPersonEnabled ? 45 : 90);
        dampAngles(this._angles, this._targetAngles, this.rotateDamping, deltaTime);

        // 第三人称模式：根据输入 move 在世界空间的方向计算角色朝向（参考 hunter-world Player）
        // 让角色朝着推杆方向走，而不是始终背对镜头
        if (this.thirdPersonEnabled && (Math.abs(move[0]) > 1e-6 || Math.abs(move[2]) > 1e-6)) {
            setYawBasis(this._angles.y, forward, right);
            const dx = right.x * move[0] + forward.x * move[2];
            const dz = right.z * move[0] + forward.z * move[2];
            if (dx * dx + dz * dz > 1e-8) {
                // PlayCanvas yaw：0° 朝 -Z，顺时针为正；yaw = atan2(-dx, -dz)
                const targetDeg = Math.atan2(-dx, -dz) * math.RAD_TO_DEG;
                const t = damp(this.avatarRotationDamping, deltaTime);
                this._avatarYaw = math.lerpAngle(
                    ((this._avatarYaw % 360) + 360) % 360,
                    ((targetDeg % 360) + 360) % 360,
                    t
                );
            }
        }

        // accumulate movement input so frames without a physics step don't lose input
        this._pendingMove[0] += move[0];
        this._pendingMove[1] = this._pendingMove[1] || move[1];
        this._pendingMove[2] += move[2];

        this._accumulator = Math.min(this._accumulator + deltaTime, MAX_SUBSTEPS * FIXED_DT);

        const numSteps = Math.floor(this._accumulator / FIXED_DT);

        if (numSteps > 0) {
            const invSteps = 1 / numSteps;
            moveStep[0] = this._pendingMove[0] * invSteps;
            moveStep[1] = this._pendingMove[1];
            moveStep[2] = this._pendingMove[2] * invSteps;

            for (let i = 0; i < numSteps; i++) {
                this._prevPosition.copy(this._position);
                this._step(FIXED_DT, moveStep);
                this._accumulator -= FIXED_DT;
            }

            this._pendingMove[0] = 0;
            this._pendingMove[1] = 0;
            this._pendingMove[2] = 0;
        }

        const alpha = this._accumulator / FIXED_DT;
        camera.position.lerp(this._prevPosition, this._position, alpha);
        // 与相机使用相同的插值流，供 Avatar 模型读取，避免"相机平滑/模型阶梯"错位抖动
        this._interpPosition.lerp(this._prevPosition, this._position, alpha);
        camera.angles.set(this._angles.x, this._angles.y, 0);
        camera.distance = this.inputDistance;
        camera.fov = this.fov;

        if (this.thirdPersonEnabled) {
            this._applyThirdPersonCamera(deltaTime, camera);
        }
    }

    private _step(dt: number, move: number[]) {
        this._jumpStarted = false;

        // ground probe: cast a ray downward to find the terrain surface
        const groundY = this._probeGround(this._position);
        const hasGround = groundY !== null;

        // jump (require release before re-triggering)
        if (this._velocity.y < 0) {
            this._jumping = false;
        }
        if (move[1] && !this._jumping && this._grounded && !this._jumpHeld) {
            this._jumping = true;
            this._velocity.y = this.jumpSpeed;
            this._grounded = false;
            this._jumpStarted = true;
            this._jumpHadInput = Math.hypot(move[0], move[2]) > 0.001;

            // 起跳水平速度截顶：限制高速（跑跳）距离，低速（走跳）不受影响
            const horiz = Math.hypot(this._velocity.x, this._velocity.z);
            if (horiz > this.maxJumpHorizontalSpeed) {
                const k = this.maxJumpHorizontalSpeed / horiz;
                this._velocity.x *= k;
                this._velocity.z *= k;
            }
        }
        this._jumpHeld = !!move[1];

        // vertical force: spring-damper when ground is detected, gravity when airborne
        if (hasGround && !this._jumping) {
            const targetY = groundY + this.hoverHeight + this.eyeHeight;
            const displacement = this._position.y - targetY;

            if (displacement > 0.1) {
                // well above target (jump/ledge): freefall, snap to rest height on arrival
                this._velocity.y -= this.gravity * dt;
                const nextY = this._position.y + this._velocity.y * dt;
                if (nextY <= targetY) {
                    this._position.y = targetY;
                    this._velocity.y = 0;
                }
                this._grounded = false;
            } else {
                // at or near target (walking/slopes): spring tracks terrain
                const springForce = -this.springStiffness * displacement - this.springDamping * this._velocity.y;
                this._velocity.y += springForce * dt;
                this._grounded = true;
            }
        } else {
            this._velocity.y -= this.gravity * dt;
            this._grounded = false;
        }

        // move
        setYawBasis(this._angles.y, forward, right);
        setBasisOffset(offset, move[0], 0, move[2], forward, right, Vec3.UP);
        this._moveMagnitude = Math.hypot(move[0], move[2]);
        this._velocity.add(offset.mulScalar(this._grounded ? this.moveGroundSpeed : this.moveAirSpeed));

        const dampFactor = this._grounded ? this.velocityDampingGround : this.velocityDampingAir;
        const alpha = damp(dampFactor, dt);
        this._velocity.x = math.lerp(this._velocity.x, 0, alpha);
        this._velocity.z = math.lerp(this._velocity.z, 0, alpha);

        this._position.add(v.copy(this._velocity).mulScalar(dt));

        // capsule collision: walls, ceiling, and fallback floor contact
        this._checkCollision(this._position, d);
    }

    onExit(_camera: Camera): void {
        // nothing to clean up
    }

    /**
     * Teleport the controller to a given camera state (used for transitions).
     *
     * @param camera - The camera state to jump to.
     */
    goto(camera: Camera) {
        // position
        this._position.copy(camera.position);
        this._prevPosition.copy(this._position);
        this._interpPosition.copy(this._position);

        // angles (clamp pitch to avoid gimbal lock)
        this._angles.set(camera.angles.x, camera.angles.y, 0);
        this._targetAngles.copy(this._angles);
        this._distance = camera.distance;
        // 初始角色 yaw 与相机一致，避免入场时生硬转
        this._avatarYaw = this._angles.y;

        // reset velocity and state
        this._resetMotion();
    }

    /**
     * Reset the controller to the spawn pose captured on the last walk-mode entry.
     *
     * @param camera - Camera state to update with the spawn pose.
     * @returns True if a spawn pose was available.
     */
    resetToSpawn(camera: Camera): boolean {
        if (!this._spawn.has) {
            return false;
        }

        this._distance = this._spawn.restore(this._position, this._angles);
        this._prevPosition.copy(this._position);
        this._interpPosition.copy(this._position);
        this._targetAngles.copy(this._angles);
        this._resetMotion();
        this._grounded = this._spawnGrounded;

        camera.position.copy(this._position);
        camera.angles.copy(this._angles);
        camera.distance = this._distance;
        camera.fov = this.fov;

        return true;
    }

    /**
     * 导航参考位置：角色眼睛位置，XZ 与角色一致（第三人称下不同于渲染相机位置）。
     *
     * @returns 角色眼睛位置向量。
     */
    get navPosition(): Vec3 {
        return this._position;
    }

    /**
     * 导航参考朝向：角色 yaw（度），第三人称下不同于渲染相机朝向。
     *
     * @returns 角色 yaw 角度（度）。
     */
    get navYaw(): number {
        // 第三人称下返回独立的角色 yaw；第一人称下仍与相机同步
        return this.thirdPersonEnabled ? this._avatarYaw : this._angles.y;
    }

    /**
     * 获取给 Avatar 使用的只读行走状态快照。
     *
     * @param result - 接收状态数据的对象。
     * @returns 写入数据后的同一个对象。
     */
    getAvatarState(result: WalkAvatarState): WalkAvatarState {
        // eyePosition 仍用最新物理位置，给上层做精确判定
        result.eyePosition.copy(this._position);
        // footPosition 用渲染插值位置，与相机渲染节奏一致，
        // 避免 fixed-substep 节奏下"相机平滑 / 模型阶梯"造成的错位抖动（移动端尤其明显）
        result.footPosition.set(
            this._interpPosition.x,
            this._interpPosition.y - this.eyeHeight,
            this._interpPosition.z
        );
        result.velocity.copy(this._velocity);
        result.grounded = this._grounded;
        result.jumping = this._jumping;
        result.jumpStarted = this._jumpStarted;
        result.jumpHadInput = this._jumpHadInput;
        // 第三人称下角色朝向与相机解耦，使用独立 yaw
        result.yaw = this.thirdPersonEnabled ? this._avatarYaw : this._angles.y;
        result.moveMagnitude = this._moveMagnitude;
        this._debugFrame += 1;
        const debugEnabled = typeof window !== 'undefined' && (window as any).sse?.config?.debug;
        if (this._debugFrame % 60 === 0 && debugEnabled) {
            console.log('[WalkController] Avatar 状态快照', {
                grounded: result.grounded,
                jumping: result.jumping,
                jumpStarted: result.jumpStarted,
                jumpHadInput: result.jumpHadInput,
                moveMagnitude: result.moveMagnitude,
                velocity: {
                    x: result.velocity.x,
                    y: result.velocity.y,
                    z: result.velocity.z
                }
            });
        }
        return result;
    }

    private _storeSpawn() {
        this._spawn.store(this._position, this._angles, this._distance);
        this._spawnGrounded = this._grounded;
    }

    private _resetMotion() {
        // 重置第三人称相机距离平滑状态，避免切模式/重生时从旧距离慢慢插值
        this._thirdPersonCurrentDistance = -1;
        this._velocity.set(0, 0, 0);
        this._grounded = false;
        this._jumping = false;
        this._jumpHeld = false;
        this._jumpStarted = false;
        this._jumpHadInput = false;
        this._pendingMove[0] = 0;
        this._pendingMove[1] = 0;
        this._pendingMove[2] = 0;
        this._accumulator = 0;
        this._moveMagnitude = 0;
    }

    private _applyThirdPersonCamera(deltaTime: number, camera: Camera) {
        avatarFoot.set(camera.position.x, camera.position.y - this.eyeHeight, camera.position.z);
        cameraFocus.copy(avatarFoot).add(cameraUpOffset.copy(Vec3.UP).mulScalar(this.thirdPersonFocusHeight));
        cameraDirection.set(0, 0, -1);
        setYawBasis(this._angles.y, forward, right);
        setBasisOffset(cameraDirection, 0, 0, 1, forward, right, Vec3.UP);
        cameraDirection.normalize();
        cameraPosition.copy(cameraFocus).sub(cameraDirection.mulScalar(this.thirdPersonDistance));
        cameraPosition.y += 0.75;

        // 期望方向向量（从 focus 指向理想相机位置）和期望距离
        const dxr = cameraPosition.x - cameraFocus.x;
        const dyr = cameraPosition.y - cameraFocus.y;
        const dzr = cameraPosition.z - cameraFocus.z;
        const desiredDist = Math.sqrt(dxr * dxr + dyr * dyr + dzr * dzr);

        // 第三人称相机防穿墙：从角色焦点沿期望方向 march，遇到第一个 non-free 体素就停下。
        // 之所以不能用 queryCapsule push-out：墙背后的可达空间也是 free 体素，相机一旦
        // 瞬移到墙后会落在 free 区，querySphere/queryCapsule 检测不到穿透，无法推回。
        // isFreeAt 是 O(1) cell lookup，整个 march 只是少量步进，性能与 queryCapsule 相当。
        let targetDist = desiredDist;
        if (this.collision && desiredDist > 1e-6) {
            const step = this.collision.voxelResolution;
            const inv = 1 / desiredDist;
            const ndx = dxr * inv;
            const ndy = dyr * inv;
            const ndz = dzr * inv;
            let safeDist = 0;
            while (safeDist + step <= desiredDist) {
                const next = safeDist + step;
                const nx = cameraFocus.x + ndx * next;
                const ny = cameraFocus.y + ndy * next;
                const nz = cameraFocus.z + ndz * next;
                if (!this.collision.isFreeAt(nx, ny, nz)) break;
                safeDist = next;
            }
            if (safeDist < desiredDist) {
                targetDist = Math.max(0.1, safeDist - step);
            }
        }

        // 平滑相机距离：避免 yaw 转动跨过墙体边缘时距离瞬变导致的视角跳跃。
        // 收近（命中墙）和伸远（墙离开）使用不同阻尼，但都是连续插值。
        if (this._thirdPersonCurrentDistance < 0) {
            this._thirdPersonCurrentDistance = targetDist;
        } else {
            const damping = targetDist < this._thirdPersonCurrentDistance ?
                this.thirdPersonRetractDamping : this.thirdPersonExtendDamping;
            const alpha = damp(damping, deltaTime);
            this._thirdPersonCurrentDistance = math.lerp(this._thirdPersonCurrentDistance, targetDist, alpha);
        }

        // 按平滑后的距离重算相机位置
        if (desiredDist > 1e-6) {
            const inv = 1 / desiredDist;
            cameraPosition.set(
                cameraFocus.x + dxr * inv * this._thirdPersonCurrentDistance,
                cameraFocus.y + dyr * inv * this._thirdPersonCurrentDistance,
                cameraFocus.z + dzr * inv * this._thirdPersonCurrentDistance
            );
        }

        camera.look(cameraPosition, cameraFocus);
        camera.distance = this.thirdPersonDistance;
    }

    /**
     * Cast multiple rays downward to find the average ground surface height.
     * Uses 5 rays (center + 4 cardinal at capsule radius) to spatially filter
     * noisy collision heights, giving the spring a smoother target.
     *
     * @param pos - Eye position in PlayCanvas world space.
     * @returns Average ground surface Y in PlayCanvas space, or null if no ground found.
     */
    private _probeGround(pos: Vec3): number | null {
        if (!this.collision) return null;

        const oy = pos.y - this.eyeHeight;
        const r = this.capsuleRadius;
        const range = this.groundProbeRange;

        let totalY = 0;
        let hitCount = 0;

        for (let i = 0; i < 5; i++) {
            let ox = pos.x;
            let oz = pos.z;
            if (i === 1) ox -= r;
            else if (i === 2) ox += r;
            else if (i === 3) oz += r;
            else if (i === 4) oz -= r;

            const hit = this.collision.queryRay(ox, oy, oz, 0, -1, 0, range);
            if (hit) {
                totalY += hit.y;
                hitCount++;
            }
        }

        return hitCount > 0 ? totalY / hitCount : null;
    }

    /**
     * Check for capsule collision and apply push-out displacement.
     * Handles walls, ceiling hits, and fallback floor contact when airborne.
     *
     * @param pos - Eye position in PlayCanvas world space.
     * @param disp - Pre-allocated vector to receive the collision push-out displacement.
     */
    private _checkCollision(pos: Vec3, disp: Vec3) {
        const center = pos.y - this.eyeHeight + this.capsuleHeight * 0.5;
        const half = this.capsuleHeight * 0.5 - this.capsuleRadius;

        if (this.collision!.queryCapsule(pos.x, center, pos.z, half, this.capsuleRadius, out)) {
            disp.set(out.x, out.y, out.z);
            pos.add(disp);

            // ceiling collision: cancel upward velocity
            if (disp.y < 0 && this._velocity.y > 0) {
                this._velocity.y = 0;
            }

            // airborne floor collision: transition to grounded as a fallback safety net
            if (!this._grounded && disp.y > 0 && this._velocity.y < 0) {
                this._velocity.y = 0;
                this._grounded = true;
            }
        }
    }
}

export { WalkController };
